import {
  CorrectValueSource,
  DiscrepancyResolutionPath,
  DiscrepancyResolutionTarget,
  ManifestDiscrepancyEventType,
} from "generated-graphql/graphql";

/** Human wording per event type. A Record forces this to stay exhaustive as members are added. */
const EVENT_TYPE_LABELS: Record<ManifestDiscrepancyEventType, string> = {
  [ManifestDiscrepancyEventType.Opened]: "Opened",
  [ManifestDiscrepancyEventType.Reopened]: "Reopened",
  [ManifestDiscrepancyEventType.ValuesChanged]: "Values changed",
  [ManifestDiscrepancyEventType.EscalationChanged]: "Escalation changed",
  [ManifestDiscrepancyEventType.Resolved]: "Resolved",
  [ManifestDiscrepancyEventType.AutoResolved]: "Auto-resolved",
  [ManifestDiscrepancyEventType.AutoIgnored]: "Auto-ignored",
  [ManifestDiscrepancyEventType.Pending]: "Marked pending",
  [ManifestDiscrepancyEventType.Ignored]: "Marked as non-discrepancy",
  [ManifestDiscrepancyEventType.RuleChanged]: "Rule changed",
};

type EventActor = {
  person?: { first?: string | null; last?: string | null } | null;
} | null;

/**
 * RESOLVED/PENDING events on a MANUAL_ENTRY row read "with a reviewer-chosen
 * value". Not "entered": MANUAL_ENTRY also covers picking the e-Manifest value
 * and applying it only to the document.
 */
const MANUAL_ENTRY_EVENT_TYPES: ReadonlySet<ManifestDiscrepancyEventType> = new Set([
  ManifestDiscrepancyEventType.Resolved,
  ManifestDiscrepancyEventType.Pending,
]);

type DescribableEvent = {
  eventType: ManifestDiscrepancyEventType;
  actorId: string;
  actor?: EventActor;
  resolutionPath?: DiscrepancyResolutionPath | null;
  correctValueSource?: CorrectValueSource | null;
  appliedTo?: DiscrepancyResolutionTarget[] | null;
};

function eventTypeLabel(event: Pick<DescribableEvent, "eventType" | "resolutionPath">): string {
  const base = EVENT_TYPE_LABELS[event.eventType];
  const isManualEntryDecision =
    MANUAL_ENTRY_EVENT_TYPES.has(event.eventType) &&
    event.resolutionPath === DiscrepancyResolutionPath.ManualEntry;
  return isManualEntryDecision ? `${base} with a reviewer-chosen value` : base;
}

const SOURCE_TEXT: Record<CorrectValueSource, string> = {
  [CorrectValueSource.Manual]: "a typed value",
  [CorrectValueSource.Vendor]: "the vendor's value",
  [CorrectValueSource.Document]: "the document's value",
  [CorrectValueSource.Emanifest]: "e-Manifest's value",
};

const WRITE_TARGET_TEXT: Partial<Record<DiscrepancyResolutionTarget, string>> = {
  [DiscrepancyResolutionTarget.VendorData]: "vendor data",
  [DiscrepancyResolutionTarget.Document]: "manifest document",
};

/** Stored copies the decision wrote to. EMANIFEST is a pending hand-fix, not a write. */
function writtenCopies(appliedTo: DiscrepancyResolutionTarget[] | null | undefined): string[] {
  return (appliedTo ?? []).flatMap((target) => WRITE_TARGET_TEXT[target] ?? []);
}

type EventLabel = { label: string; tail: string };

/**
 * The decision the reviewer made, built from what they chose rather than from
 * resolutionPath, which exists to route undo. Null when the event carries no
 * such data (older events), so the caller falls back to the type label.
 */
function reviewerDecisionLabel(event: DescribableEvent): EventLabel | null {
  if (event.actorId === "system") return null;
  const source = event.correctValueSource ? SOURCE_TEXT[event.correctValueSource] : null;
  const copies = writtenCopies(event.appliedTo);

  if (event.eventType === ManifestDiscrepancyEventType.ValuesChanged) {
    if (source == null && copies.length === 0) return null;
    const where = copies.length > 0 ? ` to ${copies.join(" and ")}` : "";
    return { label: `Wrote ${source ?? "a value"}${where}`, tail: ". Still Pending Review." };
  }
  if (source == null) return null;
  const tail = copies.length > 0 ? ` · wrote ${copies.join(" / ")}` : "";
  if (event.eventType === ManifestDiscrepancyEventType.Pending) {
    return { label: `Marked Fix pending with ${source}`, tail };
  }
  if (event.eventType === ManifestDiscrepancyEventType.Resolved) {
    return { label: `Resolved with ${source}`, tail };
  }
  return null;
}

/** A reviewer's write that left the row open: VALUES_CHANGED carrying the copies it wrote. */
function isReviewerWrite(event: DescribableEvent): boolean {
  return (
    event.eventType === ManifestDiscrepancyEventType.ValuesChanged &&
    event.actorId !== "system" &&
    writtenCopies(event.appliedTo).length > 0
  );
}

/**
 * Undoing a kept-open write records a bare reviewer VALUES_CHANGED right after
 * the write, so the pair is what identifies it.
 */
function isUndoOfWrite(event: DescribableEvent, previous: DescribableEvent | undefined): boolean {
  return (
    previous != null &&
    isReviewerWrite(previous) &&
    event.eventType === ManifestDiscrepancyEventType.ValuesChanged &&
    event.actorId !== "system" &&
    event.correctValueSource == null &&
    writtenCopies(event.appliedTo).length === 0
  );
}

/**
 * One event history line's text: the event type in words, and who did it when
 * that is known. The server resolves `actor` to null for automatic events from
 * process-manifests, whose `actorId` is the literal "system", and null when the id no
 * longer matches a user. An actor with no name is left off the line rather
 * than printed as a raw id.
 */
export function describeDiscrepancyEvent(
  event: DescribableEvent,
  /** The event just before this one, oldest-first; identifies an undo of a write. */
  previous?: DescribableEvent
): string {
  const decision = isUndoOfWrite(event, previous)
    ? { label: "Undid the write", tail: "" }
    : reviewerDecisionLabel(event);
  const label = decision?.label ?? eventTypeLabel(event);
  const tail = decision?.tail ?? "";
  if (event.actorId === "system") return `${label} automatically by Encamp`;

  const name = [event.actor?.person?.first, event.actor?.person?.last]
    .filter((part) => part != null && part !== "")
    .join(" ");
  return `${name === "" ? label : `${label} by ${name}`}${tail}`;
}
